import { useMemo } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { TIKTOK_PRIVACY_LABELS, TIKTOK_POLICY_URLS } from '../../constants/platformCompliance';

const TikTokSettingsPanel = ({ settings, onChange, capabilities, content }) => {
  const privacyLevels = capabilities?.availablePrivacyLevels || [];
  const commentDisabled = capabilities?.commentDisabled || false;
  const duetDisabled = capabilities?.duetDisabled || false;
  const stitchDisabled = capabilities?.stitchDisabled || false;
  const contentType = content?.contentType;
  const isPhoto = contentType === 'IMAGE';

  // TikTok re-validates the file when it processes the upload and rejects anything out of spec
  // (the most common being frame_rate_check_failed). Surface the requirements up front so a bad
  // file is caught before publishing rather than after a silent processing failure.
  const maxSec = capabilities?.maxVideoPostDurationSec || 0;
  const maxDurationText = maxSec > 0
    ? `Up to ${Math.floor(maxSec / 60)} min per video (your account)`
    : 'Duration limit varies by account';

  const isSelfOnly = settings.privacyLevel === 'SELF_ONLY';
  const commercialToggle = settings.commercialContentToggle || false;

  // Dynamic privacy filtering: SELF_ONLY is not allowed with branded content
  const filteredPrivacyLevels = useMemo(() => {
    if (settings.brandContent) {
      return privacyLevels.filter(level => level !== 'SELF_ONLY');
    }
    return privacyLevels;
  }, [privacyLevels, settings.brandContent]);

  // Privacy change handler — clears brandContent if SELF_ONLY selected
  const handlePrivacyChange = (value) => {
    const updates = { ...settings, privacyLevel: value };
    if (value === 'SELF_ONLY' && settings.brandContent) {
      updates.brandContent = false;
    }
    onChange(updates);
  };

  // Commercial content parent toggle
  const handleCommercialToggle = (checked) => {
    const updates = { ...settings, commercialContentToggle: checked };
    if (!checked) {
      updates.brandOrganic = false;
      updates.brandContent = false;
    }
    onChange(updates);
  };

  // Brand content sub-option — clears SELF_ONLY if enabling
  const handleBrandContentChange = (checked) => {
    const updates = { ...settings, brandContent: checked };
    if (checked && settings.privacyLevel === 'SELF_ONLY') {
      updates.privacyLevel = '';
    }
    onChange(updates);
  };

  // Validation: commercial toggle ON but no sub-option selected
  const commercialIncomplete = commercialToggle && !settings.brandOrganic && !settings.brandContent;

  // Point 4: Determine declaration text based on commercial content state
  const hasBrandedContent = settings.brandContent;

  return (
    <div className="space-y-4">
      {/* Point 1a: Creator Info — display creator's nickname */}
      {capabilities?.creatorNickname && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
          {capabilities.creatorAvatarUrl && (
            <img src={capabilities.creatorAvatarUrl} alt="" className="w-10 h-10 rounded-full" />
          )}
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{capabilities.creatorNickname}</p>
            <p className="text-xs text-gray-500">TikTok Creator</p>
          </div>
        </div>
      )}

      {/* Point 1b: Posting restriction — if capabilities failed to load or posting limit reached */}
      {capabilities?.postingRestricted && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-start gap-2">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-red-700 dark:text-red-300">Posting limit reached</p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
              You have reached TikTok&apos;s posting limit. Please try again later.
            </p>
          </div>
        </div>
      )}

      {/* Video requirements — TikTok rejects out-of-spec files during processing (most often
          frame_rate_check_failed), so show the specs before publishing, not after. */}
      {!isPhoto && (
        <div className="p-3 rounded-lg bg-sky-50 dark:bg-sky-900/15 border border-sky-100 dark:border-sky-900/30">
          <p className="text-xs font-semibold text-sky-800 dark:text-sky-300 flex items-center gap-1.5">
            <InformationCircleIcon className="w-4 h-4 flex-shrink-0" /> TikTok video requirements
          </p>
          <ul className="mt-1.5 text-xs text-sky-700/90 dark:text-sky-300/80 space-y-0.5 list-disc list-inside">
            <li>Frame rate <span className="font-semibold">23–60 FPS</span> — the most common cause of rejection</li>
            <li>MP4 or MOV, H.264/H.265, up to 1080p</li>
            <li>{maxDurationText}</li>
          </ul>
          <p className="mt-1.5 text-[11px] text-sky-600/80 dark:text-sky-300/60">
            TikTok re-checks these while processing; a video outside spec is rejected after upload.
            Re-encode to 30 FPS if unsure.
          </p>
        </div>
      )}

      {/* Point 2a: Title — user must be able to enter a title */}
      <div>
        <label className="label">Title / Caption</label>
        <input
          type="text"
          className="input w-full"
          placeholder={content?.title || 'Enter a title for your TikTok post...'}
          value={settings.captionOverride || ''}
          onChange={e => onChange({ ...settings, captionOverride: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-1">
          {settings.captionOverride
            ? 'Custom title will be used for this TikTok post'
            : `Default: "${content?.title || 'No title'}" — edit above to override`}
        </p>
      </div>

      {/* Point 2b: Privacy Level — NO default, options from creator_info */}
      <div>
        <label className="label">Who can view this {isPhoto ? 'photo' : 'video'}? <span className="text-red-500">*</span></label>
        <select
          className="input w-full"
          value={settings.privacyLevel || ''}
          onChange={e => handlePrivacyChange(e.target.value)}
        >
          <option value="" disabled>Select privacy level...</option>
          {filteredPrivacyLevels.map(level => (
            <option key={level} value={level}>
              {TIKTOK_PRIVACY_LABELS[level] || level}
            </option>
          ))}
        </select>
        {!settings.privacyLevel && (
          <p className="text-xs text-amber-600 mt-1">You must select a privacy level before publishing</p>
        )}
        {settings.brandContent && (
          <p className="text-xs text-gray-500 mt-1">
            &quot;Only Me&quot; is not available for branded content
          </p>
        )}
      </div>

      {/* Point 2c: Interaction Settings — checkboxes, unchecked by default, greyed if disabled */}
      <div className="space-y-2">
        <label className="label">Allow users to:</label>

        <label className={`flex items-center gap-3 p-2 rounded ${commentDisabled ? 'opacity-50' : ''}`}>
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300"
            checked={settings.disableComment === false}
            disabled={commentDisabled}
            onChange={e => onChange({ ...settings, disableComment: !e.target.checked })}
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Comment {commentDisabled && <span className="text-xs text-gray-400">(disabled in creator settings)</span>}
          </span>
        </label>

        {!isPhoto && (
          <>
            <label className={`flex items-center gap-3 p-2 rounded ${duetDisabled ? 'opacity-50' : ''}`}>
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={settings.disableDuet === false}
                disabled={duetDisabled}
                onChange={e => onChange({ ...settings, disableDuet: !e.target.checked })}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Duet {duetDisabled && <span className="text-xs text-gray-400">(disabled in creator settings)</span>}
              </span>
            </label>

            <label className={`flex items-center gap-3 p-2 rounded ${stitchDisabled ? 'opacity-50' : ''}`}>
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={settings.disableStitch === false}
                disabled={stitchDisabled}
                onChange={e => onChange({ ...settings, disableStitch: !e.target.checked })}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Stitch {stitchDisabled && <span className="text-xs text-gray-400">(disabled in creator settings)</span>}
              </span>
            </label>
          </>
        )}
      </div>

      {/* Point 3a: Commercial Content Disclosure — Toggle + Sub-options */}
      <div className="space-y-3">
        <label className="label">Commercial Content Disclosure</label>

        {/* Parent toggle — exact TikTok label */}
        <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-dark-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors">
          <div className="relative inline-flex items-center flex-shrink-0">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={commercialToggle}
              onChange={e => handleCommercialToggle(e.target.checked)}
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-dark-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-dark-600 peer-checked:bg-primary-600" />
          </div>
          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Disclose {isPhoto ? 'photo' : 'video'} content
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              Indicate whether this content promotes yourself, a brand, product or service
            </p>
          </div>
        </label>

        {/* Sub-options — revealed when toggle is ON */}
        {commercialToggle && (
          <div className="ml-4 pl-4 border-l-2 border-primary-200 dark:border-primary-800 space-y-3">
            {/* Your Brand */}
            <label className="flex items-start gap-3 p-2 rounded cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 mt-0.5"
                checked={settings.brandOrganic || false}
                onChange={e => onChange({ ...settings, brandOrganic: e.target.checked })}
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Your Brand</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  You are promoting yourself or your own business
                </p>
              </div>
            </label>
            {settings.brandOrganic && (
              <p className="text-xs text-amber-600 dark:text-amber-400 ml-9 -mt-1">
                Your {isPhoto ? 'photo' : 'video'} will be labeled as &quot;Promotional content&quot;
              </p>
            )}

            {/* Branded Content — Point 3b: disabled when SELF_ONLY */}
            <label className={`flex items-start gap-3 p-2 rounded ${isSelfOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              title={isSelfOnly ? 'Branded content visibility cannot be set to private.' : undefined}
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 mt-0.5"
                checked={settings.brandContent || false}
                disabled={isSelfOnly}
                onChange={e => handleBrandContentChange(e.target.checked)}
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Branded Content</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  You are promoting another brand or a third party
                </p>
              </div>
            </label>
            {settings.brandContent && !settings.brandOrganic && (
              <p className="text-xs text-amber-600 dark:text-amber-400 ml-9 -mt-1">
                Your {isPhoto ? 'photo' : 'video'} will be labeled as &quot;Paid partnership&quot;
              </p>
            )}
            {settings.brandContent && settings.brandOrganic && (
              <p className="text-xs text-amber-600 dark:text-amber-400 ml-9 -mt-1">
                Your {isPhoto ? 'photo' : 'video'} will be labeled as &quot;Paid partnership&quot;
              </p>
            )}

            {/* Point 3b: Explanation when SELF_ONLY blocks Branded Content */}
            {isSelfOnly && (
              <p className="text-xs text-amber-600 dark:text-amber-400 ml-9 -mt-1">
                Branded content visibility cannot be set to private. Change privacy level to enable branded content.
              </p>
            )}

            {/* Validation warning: toggle ON but no sub-option selected */}
            {commercialIncomplete && (
              <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center gap-2">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                </svg>
                <p className="text-xs text-red-700 dark:text-red-300">
                  You need to indicate if your content promotes yourself, a third party, or both.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Point 4 (4a-4d): Compliance Declarations — exact TikTok wording */}
      <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded-lg text-xs text-gray-600 dark:text-gray-400">
        {hasBrandedContent ? (
          <p>
            By posting, you agree to TikTok&apos;s{' '}
            <a
              href={TIKTOK_POLICY_URLS.BRANDED_CONTENT}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium text-gray-700 dark:text-gray-300"
            >
              Branded Content Policy
            </a>
            {' '}and{' '}
            <a
              href={TIKTOK_POLICY_URLS.MUSIC_USAGE}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium text-gray-700 dark:text-gray-300"
            >
              Music Usage Confirmation
            </a>.
          </p>
        ) : (
          <p>
            By posting, you agree to TikTok&apos;s{' '}
            <a
              href={TIKTOK_POLICY_URLS.MUSIC_USAGE}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium text-gray-700 dark:text-gray-300"
            >
              Music Usage Confirmation
            </a>.
          </p>
        )}
      </div>
    </div>
  );
};

export default TikTokSettingsPanel;
